import {
  COLUMN_LEVEL_SEPARATOR,
  TOTALS_COLUMN_LABEL,
  getColumnGroupName,
  parseColumnPathKey,
  isMultiHierarchyCalculatedColumnGroup,
  type ColumnValue,
  type IColumnSource,
  type MultiHierarchyCalculatedColumnGroup
} from '@opengov/reporting-types';
import { parseFormulaReferences } from './FormulaInput.utils';
import { buildColumnOptions } from './hooks/useBuildColumnOptions';

/** A calculated column whose formula names a column the report no longer has. */
export type BrokenCalcColumn = {
  id: string;
  alias: string;
  hidden: boolean;
  /** One readable label per missing reference, e.g. `Actuals · 2026-10-01`. */
  missingReferences: string[];
};

const TOTALS_REFERENCE_ID = 'totals';
const DELETED_COLUMN_GROUP_LABEL = 'a deleted column group';

type FormulaReference = { parentGroupId: string; groupId: string };

/**
 * Finds the calculated columns that will render blank because a column their formula references is
 * absent from `pivotColumns`.
 *
 * Formulas store literal `@columnGroupId:valueId` references, and a column group's values come from
 * its data, so a change to organize-by, filters, date range, as-of date, or data source — or simply a
 * period with no rows — can remove a column a formula was built against. The service renders such a
 * calc column blank rather than failing the request (IRD-1562), and so does a calc column that
 * references a blank one; this mirrors that so the UI can say why.
 *
 * Validity is judged by `buildColumnOptions`, the same function that offers references in the
 * formula picker, so "valid" here and "pickable" there cannot disagree.
 *
 * `pivotColumns` must be a completed column list: an empty one means the report has no columns, so
 * every data reference is missing. A caller holding columns that have not loaded yet must not ask.
 */
export function findBrokenCalcColumns({
  columnGroups,
  pivotColumns
}: {
  columnGroups: IColumnSource[];
  pivotColumns: ColumnValue[];
}): BrokenCalcColumn[] {
  const calcColumns = columnGroups.filter(isMultiHierarchyCalculatedColumnGroup);
  if (calcColumns.length === 0) {
    return [];
  }

  const calcById = new Map(calcColumns.map((calc) => [calc.id, calc]));
  // Calc-to-calc references never reach this set: `isMissingDataReference` skips them first.
  const validDataKeys = new Set(
    buildColumnOptions({ pivotColumns, columnGroups, excludeColumnGroupIds: [] }).map(referenceKey)
  );
  const columnGroupById = new Map(columnGroups.map((group) => [group.id, group]));

  const missingByCalcId = new Map<string, string[]>();
  const resolveMissing = (calc: MultiHierarchyCalculatedColumnGroup, visiting: Set<string>): string[] => {
    const known = missingByCalcId.get(calc.id);
    if (known) return known;
    // A cycle is the service's error to report (CircularReferenceError), not a missing column.
    if (visiting.has(calc.id)) return [];

    const references = parseFormulaReferences(calc.formula);
    const missingData = references
      .filter((ref) => isMissingDataReference(ref, calcById, validDataKeys))
      .map((ref) => describeDataReference(ref, columnGroupById));
    // With no data reference missing, it is still blank if a calc column it references is blank.
    const missing =
      missingData.length > 0
        ? missingData
        : blankCalcDependencies(references, calcById, (dependency) =>
            resolveMissing(dependency, new Set([...visiting, calc.id]))
          );
    missingByCalcId.set(calc.id, missing);
    return missing;
  };

  return calcColumns
    .map((calc) => ({
      id: calc.id,
      alias: calc.alias,
      hidden: calc.hidden ?? false,
      missingReferences: resolveMissing(calc, new Set())
    }))
    .filter((broken) => broken.missingReferences.length > 0);
}

type ReportColumns = { columnGroups: IColumnSource[]; pivotColumns: ColumnValue[] };

/**
 * The visible calc columns a change would newly blank: broken after it, but not already before it.
 * A calc column that was already blank is not news, so it does not hold up the change.
 */
export function findNewlyBrokenCalcColumns({
  before,
  after
}: {
  before: ReportColumns;
  after: ReportColumns;
}): BrokenCalcColumn[] {
  const alreadyBroken = new Set(findBrokenCalcColumns(before).map((broken) => broken.id));
  return findBrokenCalcColumns(after).filter((broken) => !broken.hidden && !alreadyBroken.has(broken.id));
}

function isMissingDataReference(
  ref: FormulaReference,
  calcById: Map<string, MultiHierarchyCalculatedColumnGroup>,
  validDataKeys: Set<string>
): boolean {
  return !calcById.has(ref.parentGroupId) && !validDataKeys.has(referenceKey(ref));
}

/** The aliases of the calc columns these references name that are themselves blank. */
function blankCalcDependencies(
  references: FormulaReference[],
  calcById: Map<string, MultiHierarchyCalculatedColumnGroup>,
  resolveDependency: (dependency: MultiHierarchyCalculatedColumnGroup) => string[]
): string[] {
  return references
    .map((ref) => calcById.get(ref.parentGroupId))
    .filter((dependency): dependency is MultiHierarchyCalculatedColumnGroup => dependency !== undefined)
    .filter((dependency) => resolveDependency(dependency).length > 0)
    .map((dependency) => dependency.alias);
}

function referenceKey(ref: FormulaReference): string {
  return `${ref.parentGroupId}:${ref.groupId}`;
}

function describeDataReference(ref: FormulaReference, columnGroupById: Map<string, IColumnSource>): string {
  const group = columnGroupById.get(ref.parentGroupId);
  if (!group) {
    return DELETED_COLUMN_GROUP_LABEL;
  }
  const valueLabel = ref.groupId === TOTALS_REFERENCE_ID ? TOTALS_COLUMN_LABEL : describeColumnPath(ref.groupId);
  const groupName = getColumnGroupName(group);
  return groupName ? `${groupName} · ${valueLabel}` : valueLabel;
}

/**
 * A reference's value id is its column's level path, built by `buildColumnPathKey` and then
 * percent-encoded. Shown one level per segment (`FY2026 › Q4`), not with the key's raw delimiter. A
 * malformed encoding is shown as stored.
 */
function describeColumnPath(encodedPathKey: string): string {
  try {
    return parseColumnPathKey(decodeURIComponent(encodedPathKey)).join(COLUMN_LEVEL_SEPARATOR);
  } catch {
    return encodedPathKey;
  }
}
